import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { Button, Card, ErrorAlert } from '../lib/ui';

export interface PhotoDto {
  id: number;
  structureId: number;
  name: string;
  url: string;
  createdAt: string | null;
}

const MAX_PHOTOS = 10;
const ACCEPT = 'image/jpeg,image/png';

export function StructurePhotosCard({ structureId }: { structureId: number }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const photosQuery = useQuery({
    queryKey: ['structure-photos', structureId],
    queryFn: () => api.get<PhotoDto[]>(`/api/v1/structures/${structureId}/photos`),
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const fd = new FormData();
      for (const f of files) fd.append('photos', f);
      const res = await fetch(`/api/v1/structures/${structureId}/photos`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const body = await res.text();
        throw new ApiError(res.status, body, body || `Upload failed (${res.status})`);
      }
      return (await res.json()) as PhotoDto[];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['structure-photos', structureId] }),
    onError: (e) => setError(e instanceof Error ? e.message : 'Upload failed'),
  });

  const remove = useMutation({
    mutationFn: (photoId: number) =>
      api.del(`/api/v1/structures/${structureId}/photos/${photoId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['structure-photos', structureId] }),
    onError: (e) => setError(e instanceof Error ? e.message : 'Delete failed'),
  });

  const removeAll = useMutation({
    mutationFn: () => api.del(`/api/v1/structures/${structureId}/photos`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['structure-photos', structureId] }),
    onError: (e) => setError(e instanceof Error ? e.message : 'Delete failed'),
  });

  const photos = photosQuery.data ?? [];
  const busy = upload.isPending || remove.isPending || removeAll.isPending;
  const canUpload = photos.length < MAX_PHOTOS && !busy;

  const onPick = () => {
    setError(null);
    inputRef.current?.click();
  };
  const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    // Snapshot the live FileList into an array BEFORE clearing the input.
    // Clearing input.value empties the live FileList, so the async mutation
    // would otherwise see zero files ("No files supplied").
    const files = Array.from(e.target.files);
    upload.mutate(files);
    e.target.value = ''; // allow same-file re-selection
  };

  return (
    <Card title={`Photos · ${photos.length} / ${MAX_PHOTOS}`} actions={
      <div className="d-flex gap-2">
        {photos.length > 0 && (
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => {
              if (!confirm('Delete all photos for this structure?')) return;
              setError(null);
              removeAll.mutate();
            }}
          >
            Delete All
          </Button>
        )}
        <Button variant="secondary" disabled={!canUpload} onClick={onPick}>
          {upload.isPending ? 'Uploading…' : '+ Upload'}
        </Button>
      </div>
    }>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={onFiles}
      />

      {photosQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : photos.length === 0 ? (
        <p className="text-sm text-slate-500">No photos yet. JPEG or PNG, up to 8 MB each.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {photos.map((p) => (
            <li key={p.id} className="group relative overflow-hidden rounded-md border border-slate-200 bg-slate-50">
              <img
                src={p.url}
                alt={p.name}
                className="h-32 w-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => {
                  if (!confirm('Delete this photo?')) return;
                  setError(null);
                  remove.mutate(p.id);
                }}
                disabled={busy}
                className="absolute right-1 top-1 rounded bg-white/90 px-2 py-0.5 text-xs font-medium text-red-700 opacity-0 shadow-sm transition group-hover:opacity-100"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
      <ErrorAlert message={error} />
    </Card>
  );
}
